import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, MessageSquare, Users, HeadphonesIcon, Zap, Shield } from 'lucide-react'
import Navbar from '../components/Common/Navbar'
import Footer from '../components/Common/Footer'

const B = 'https://pixner.net/bankio/main/assets/images'

const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  )
}

export default function ContactUs() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)

  const onChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))

  const onSubmit = async e => {
    e.preventDefault()
    setLoading(true)
    try {
      const res = await fetch('https://api.a1xpay.com/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (res.ok) { setStatus('success'); setForm({ name: '', email: '', subject: '', message: '' }) }
      else setStatus('error')
    } catch {
      setStatus('error')
    }
    setLoading(false)
  }

  return (
    <>
      <style>{css}</style>
      <div className="ct-root">
        <Navbar />

        {/* Hero */}
        <section className="ct-hero">
          <div className="ct-container ct-hero-grid">
            <motion.div className="ct-hero-text"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
              <span className="ct-sub">Contact us</span>
              <h1 className="ct-hero-h1">We'd love to<br />hear from you</h1>
              <p className="ct-hero-p">
                Have a question, a partnership proposal, or just want to say hello? Our team is ready to help within 2 hours.
              </p>
              <div className="ct-hero-pills">
                {[{ icon: <Zap size={13}/>, t: '2hr Response' }, { icon: <HeadphonesIcon size={13}/>, t: '24/7 Support' }, { icon: <Users size={13}/>, t: 'Dedicated Team' }].map(p => (
                  <span key={p.t} className="ct-hero-pill">{p.icon}{p.t}</span>
                ))}
              </div>
            </motion.div>
            <motion.div className="ct-hero-visual"
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
              <div className="ct-hv-card">
                <div className="ct-hv-header">
                  <div className="ct-hv-dots"><span/><span/><span/></div>
                  <span className="ct-hv-title">Support Dashboard</span>
                </div>
                <div className="ct-hv-stats">
                  {[{ v: '2hr', l: 'Avg Response' }, { v: '98%', l: 'Satisfaction' }, { v: '24/7', l: 'Available' }].map(s => (
                    <div key={s.l} className="ct-hv-stat">
                      <div className="ct-hv-stat-val">{s.v}</div>
                      <div className="ct-hv-stat-lbl">{s.l}</div>
                    </div>
                  ))}
                </div>
                <div className="ct-hv-channels">
                  {[{ icon: <Phone size={14}/>, ch: 'Phone Support', t: 'Mon–Sat 10AM–7PM' },
                    { icon: <Mail size={14}/>, ch: 'Email', t: 'support@a1xpay.com' },
                    { icon: <MessageSquare size={14}/>, ch: 'Live Chat', t: 'Available now' }].map(c => (
                    <div key={c.ch} className="ct-hv-ch">
                      <div className="ct-hv-ch-icon">{c.icon}</div>
                      <div>
                        <div className="ct-hv-ch-name">{c.ch}</div>
                        <div className="ct-hv-ch-sub">{c.t}</div>
                      </div>
                      <div className="ct-hv-ch-dot" />
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Contact grid */}
        <section className="ct-section ct-bg-white">
          <div className="ct-container ct-grid">

            {/* Info panel */}
            <FadeUp className="ct-info">
              <div className="ct-head-circle"><MessageSquare size={26} color="#fff" /></div>
              <span className="ct-sub ct-green">Get in touch</span>
              <h2 className="ct-section-h2">Contact Information</h2>
              <p className="ct-para">
                Reach out to us by phone, email, or drop by. We're here Monday–Saturday.
              </p>
              <div className="ct-info-list">
                <div className="ct-info-item">
                  <div className="ct-info-icon-wrap"><Phone size={18} /></div>
                  <div>
                    <div className="ct-info-label">Phone</div>
                    <a href="tel:+919910373510" className="ct-info-val">+91 99103 73510</a>
                  </div>
                </div>
                <div className="ct-info-item">
                  <div className="ct-info-icon-wrap"><Mail size={18} /></div>
                  <div>
                    <div className="ct-info-label">Email</div>
                    <a href="mailto:support@a1xpay.com" className="ct-info-val">support@a1xpay.com</a>
                  </div>
                </div>
                <div className="ct-info-item">
                  <div className="ct-info-icon-wrap"><MapPin size={18} /></div>
                  <div>
                    <div className="ct-info-label">Address</div>
                    <div className="ct-info-val">Delhi NCR, India</div>
                  </div>
                </div>
                <div className="ct-info-item">
                  <div className="ct-info-icon-wrap"><Clock size={18} /></div>
                  <div>
                    <div className="ct-info-label">Business hours</div>
                    <div className="ct-info-val">Mon–Sat, 10 AM–7 PM IST</div>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Form */}
            <FadeUp delay={0.1} className="ct-form-wrap">
              <div className="ct-form-card">
                <h3 className="ct-form-title">Send us a message</h3>
                {status === 'success' ? (
                  <div className="ct-success">
                    <CheckCircle2 size={40} className="ct-success-icon" />
                    <h4>Message sent!</h4>
                    <p>We'll get back to you within 2 business hours.</p>
                    <button className="ct-btn-primary" onClick={() => setStatus(null)}>Send another</button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="ct-form">
                    <div className="ct-form-row">
                      <div className="ct-field">
                        <label className="ct-label">Full Name</label>
                        <input className="ct-input" name="name" value={form.name} onChange={onChange} placeholder="Your name" required />
                      </div>
                      <div className="ct-field">
                        <label className="ct-label">Email</label>
                        <input className="ct-input" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@company.com" required />
                      </div>
                    </div>
                    <div className="ct-field">
                      <label className="ct-label">Subject</label>
                      <input className="ct-input" name="subject" value={form.subject} onChange={onChange} placeholder="How can we help?" required />
                    </div>
                    <div className="ct-field">
                      <label className="ct-label">Message</label>
                      <textarea className="ct-input ct-textarea" name="message" value={form.message} onChange={onChange} placeholder="Tell us more..." rows={5} required />
                    </div>
                    {status === 'error' && <p className="ct-error">Something went wrong. Please try again or email us directly.</p>}
                    <button type="submit" className="ct-btn-primary ct-submit" disabled={loading}>
                      {loading ? 'Sending...' : <><Send size={15} /> Send message</>}
                    </button>
                  </form>
                )}
              </div>
            </FadeUp>
          </div>
        </section>

        {/* Security + trust section */}
        <section className="ct-trust-section">
          <div className="ct-container ct-trust-grid">
            <FadeUp className="ct-trust-text">
              <div className="ct-head-circle"><Shield size={26} color="#fff" /></div>
              <span className="ct-sub ct-green">Safe & secure</span>
              <h2 className="ct-section-h2">Your data is always protected</h2>
              <p className="ct-para">All form submissions are encrypted end-to-end. We never share your contact details with third parties, and we'll only contact you about the specific matter you reached out about.</p>
              <div className="ct-trust-badges">
                {['256-bit SSL Encrypted', 'No spam, ever', 'DPDPA compliant', 'Reply in 2 hours'].map(b => (
                  <span key={b} className="ct-badge"><CheckCircle2 size={13} color="#1A4DBE" /> {b}</span>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={0.1} className="ct-trust-img">
              <motion.img src={`${B}/security-left.png`} alt="Secure contact"
                whileHover={{ scale: 1.03 }} transition={{ type: 'spring', stiffness: 180 }} />
            </FadeUp>
          </div>
        </section>

        {/* Get started CTA */}
        <section className="ct-cta">
          <div className="ct-container ct-cta-flex">
            <FadeUp className="ct-cta-text">
              <span className="ct-sub" style={{color:'rgba(255,255,255,0.75)'}}>Ready to get started?</span>
              <h2 className="ct-cta-h2">Open your account in under 24 hours</h2>
              <Link to="/register" className="ct-btn-white">Create free account</Link>
            </FadeUp>
            <motion.div className="ct-cta-illu"
              initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}>
              <img src={`${B}/get-start.png`} alt="Get started" />
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;800;900&display=swap');
.ct-root { font-family: 'Jost', sans-serif; background: #fff; }
.ct-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
@media (min-width:1200px) { .ct-container { padding: 0 2.5rem; } }
.ct-hero { background: linear-gradient(135deg, #f5faff 0%, #eef3ff 100%); padding: 100px 0 80px; }
.ct-hero-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; align-items: center; }
@media (min-width:1024px) { .ct-hero-grid { grid-template-columns: 1fr 1fr; gap: 4rem; } }
.ct-hero-text {}
.ct-sub { font-size: 0.875rem; font-weight: 700; color: #1A4DBE; margin-bottom: 0.875rem; display: block; text-transform: uppercase; letter-spacing: 0.08em; }
.ct-green { color: #1A4DBE; }
.ct-hero-h1 { font-size: clamp(2rem, 4.5vw, 3.25rem); font-weight: 800; color: #0A1023; letter-spacing: -0.04em; line-height: 1.15; margin-bottom: 1.25rem; }
.ct-hero-p { font-size: 1.0625rem; color: #2C3655; line-height: 1.75; margin-bottom: 1.5rem; }
.ct-hero-pills { display: flex; gap: 0.625rem; flex-wrap: wrap; }
.ct-hero-pill { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.35rem 0.875rem; background: #fff; border: 1.5px solid #E8EBF4; border-radius: 50px; font-size: 0.8125rem; font-weight: 600; color: #1A4DBE; }
/* Hero visual */
.ct-hero-visual { display: none; }
@media (min-width:1024px) { .ct-hero-visual { display: block; } }
.ct-hv-card { background: #fff; border-radius: 1.25rem; padding: 1.75rem; box-shadow: 0 20px 60px rgba(26,77,190,0.15); border: 1px solid #E8EBF4; }
.ct-hv-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
.ct-hv-dots { display: flex; gap: 5px; }
.ct-hv-dots span { width: 10px; height: 10px; border-radius: 50%; background: #E8EBF4; }
.ct-hv-dots span:first-child { background: #F87171; }
.ct-hv-dots span:nth-child(2) { background: #FBBF24; }
.ct-hv-dots span:nth-child(3) { background: #34D399; }
.ct-hv-title { font-size: 0.875rem; font-weight: 700; color: #0A1023; }
.ct-hv-stats { display: grid; grid-template-columns: repeat(3,1fr); gap: 1rem; margin-bottom: 1.5rem; padding: 1rem; background: #f5faff; border-radius: 0.75rem; }
.ct-hv-stat { text-align: center; }
.ct-hv-stat-val { font-size: 1.375rem; font-weight: 800; color: #1A4DBE; }
.ct-hv-stat-lbl { font-size: 0.6875rem; color: #64748B; font-weight: 500; margin-top: 0.2rem; }
.ct-hv-channels { display: flex; flex-direction: column; gap: 0.75rem; }
.ct-hv-ch { display: flex; align-items: center; gap: 0.875rem; padding: 0.75rem; background: #f8faff; border-radius: 0.75rem; border: 1px solid #E8EBF4; }
.ct-hv-ch-icon { width: 32px; height: 32px; background: linear-gradient(135deg,#1A4DBE,#06B6D4); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
.ct-hv-ch-name { font-size: 0.8125rem; font-weight: 700; color: #0A1023; }
.ct-hv-ch-sub { font-size: 0.75rem; color: #64748B; }
.ct-hv-ch-dot { width: 8px; height: 8px; border-radius: 50%; background: #22C55E; margin-left: auto; flex-shrink: 0; }
.ct-section { padding: 80px 0; }
.ct-bg-white { background: #fff; }
.ct-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; align-items: start; }
@media (min-width:1024px) { .ct-grid { grid-template-columns: 5fr 7fr; gap: 5rem; } }
.ct-head-circle { width: 60px; height: 60px; border-radius: 50%; background: linear-gradient(135deg,#1A4DBE,#06B6D4); display: flex; align-items: center; justify-content: center; margin-bottom: 1.5rem; box-shadow: 0 8px 24px rgba(26,77,190,0.3); }
.ct-section-h2 { font-size: clamp(1.75rem, 3.5vw, 2.25rem); font-weight: 700; color: #0A1023; letter-spacing: -0.03em; margin-bottom: 1rem; }
.ct-para { font-size: 1rem; color: #2C3655; line-height: 1.75; margin-bottom: 2rem; }
.ct-info-list { display: flex; flex-direction: column; gap: 1.25rem; }
.ct-info-item { display: flex; align-items: flex-start; gap: 0.875rem; }
.ct-info-icon-wrap { width: 2.5rem; height: 2.5rem; background: #EEF3FF; border-radius: 0.5rem; display: flex; align-items: center; justify-content: center; color: #1A4DBE; flex-shrink: 0; }
.ct-info-label { font-size: 0.8125rem; color: #64748B; font-weight: 500; margin-bottom: 0.2rem; }
.ct-info-val { font-size: 0.9375rem; font-weight: 600; color: #0A1023; text-decoration: none; }
a.ct-info-val:hover { color: #1A4DBE; }
.ct-form-card { background: #f5faff; border-radius: 1.25rem; padding: 2.5rem 2rem; box-shadow: 0 4px 24px rgba(47,65,129,0.08); }
.ct-form-title { font-size: 1.375rem; font-weight: 700; color: #0A1023; margin-bottom: 1.75rem; }
.ct-form { display: flex; flex-direction: column; gap: 1.25rem; }
.ct-form-row { display: grid; grid-template-columns: 1fr; gap: 1.25rem; }
@media (min-width:600px) { .ct-form-row { grid-template-columns: 1fr 1fr; } }
.ct-field { display: flex; flex-direction: column; gap: 0.375rem; }
.ct-label { font-size: 0.875rem; font-weight: 600; color: #0A1023; }
.ct-input { padding: 0.7rem 1rem; border: 1.5px solid #D8DFEE; border-radius: 0.625rem; font-size: 0.9375rem; font-family: 'Jost', sans-serif; color: #0A1023; background: #fff; outline: none; transition: border-color 0.15s; }
.ct-input:focus { border-color: #1A4DBE; }
.ct-textarea { resize: vertical; min-height: 120px; }
.ct-error { font-size: 0.875rem; color: #DC2626; }
.ct-btn-primary { display: inline-flex; align-items: center; gap: 0.5rem; justify-content: center; padding: 0.75rem 1.75rem; font-size: 1rem; font-weight: 600; color: #fff; background: #1A4DBE; border: none; border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; cursor: pointer; }
.ct-btn-primary:hover:not(:disabled) { background: #1540a8; transform: translateY(-1px); }
.ct-btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }
.ct-submit { width: 100%; }
.ct-success { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 0.75rem; padding: 2rem 0; }
.ct-success-icon { color: #055C2D; }
.ct-success h4 { font-size: 1.375rem; font-weight: 700; color: #0A1023; }
.ct-success p { font-size: 1rem; color: #2C3655; }
/* Trust section */
.ct-trust-section { padding: 80px 0; background: #f5faff; }
.ct-trust-grid { display: grid; grid-template-columns: 1fr; gap: 3rem; align-items: center; }
@media (min-width:1024px) { .ct-trust-grid { grid-template-columns: 1fr 1fr; gap: 5rem; } }
.ct-trust-img { display: flex; align-items: center; justify-content: center; }
.ct-trust-img img { max-width: 100%; max-height: 380px; object-fit: contain; }
.ct-trust-badges { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 1.25rem; }
.ct-badge { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.8125rem; font-weight: 600; color: #1A4DBE; background: #eef3ff; border: 1px solid #B0BAF1; border-radius: 50px; padding: 0.3rem 0.75rem; }
/* CTA section */
.ct-cta { background: linear-gradient(135deg, #1A4DBE 0%, #1540a8 100%); padding: 80px 0; position: relative; overflow: hidden; }
.ct-cta::before { content: ''; position: absolute; width: 400px; height: 400px; border-radius: 50%; background: rgba(255,255,255,0.05); top: -150px; right: -80px; }
.ct-cta-flex { position: relative; z-index: 2; display: flex; align-items: center; justify-content: space-between; gap: 2rem; flex-wrap: wrap; }
.ct-cta-text { flex: 1; min-width: 280px; }
.ct-cta-h2 { font-size: clamp(1.875rem, 4vw, 2.75rem); font-weight: 800; color: #fff; letter-spacing: -0.04em; margin-bottom: 2rem; }
.ct-cta-illu { display: none; }
@media (min-width:900px) { .ct-cta-illu { display: block; width: 200px; flex-shrink: 0; } }
.ct-cta-illu img { max-width: 100%; filter: brightness(1.1) drop-shadow(0 8px 24px rgba(0,0,0,0.2)); }
.ct-btn-white { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.7rem 1.75rem; font-size: 1rem; font-weight: 600; color: #1A4DBE; background: #fff; border: 1.5px solid #fff; border-radius: 50px; text-decoration: none; transition: all 0.2s; font-family: 'Jost', sans-serif; box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
.ct-btn-white:hover { background: #f0f4ff; }
`
